import React from 'react';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { EnrichedAdditionalPrivilegeItem } from '../PrivilegeConfig.types';
import { AdditionalPrivilegeContainer } from './AdditionalPrivilegeContainer';
import { AdditionalPrivilegeTree } from './AdditionalPrivilegeTree';
import { RequiredPrivilegeHints } from './RequiredPrivilegeHints';
import { Icons } from '../../../components/atoms/SVG';

type AdditionalPrivilegeTreeNodeProps = {
  privilegeItem: EnrichedAdditionalPrivilegeItem;
  /**
   * the translated names of all parent privileges to be displayed in the "requires: ..." part.
   */
  parentAdditionalPrivilegeLabels: string[];
};

/**
 * Represents a basic privilege which is granted to a role.
 * Each additional privilege may have dependent (child) additional privileges which are rendered as child components.
 * It may also display the additonally required basic privileges.
 * All parent additional privileges in the tree as implicitly also privileges that this one depends on.
 * @param privilegeItem The privilege to display.
 * @param parentAdditionalPrivilegeLabels The titles of all parents privileges in the tree
 *                                        (implicit privilege dependencies)
 */
export function AdditionalPrivilegeTreeNode({
  privilegeItem,
  parentAdditionalPrivilegeLabels,
}: AdditionalPrivilegeTreeNodeProps) {
  const { t } = useSonTranslation('PrivilegeConfig');

  return (
    <>
      <AdditionalPrivilegeContainer key={privilegeItem.privilegeType} privilege={privilegeItem}>
        <div className="privilege-config__additional-privilege">
          <div className="privilege-config__additional-privilege-hint font-regular-13-19">
            {/* privilege description */}
            <p>{t(`input.${privilegeItem.privilegeType.toLowerCase()}.description` as any)}</p>

            {/* comma-separated list of required other privileges (optional) */}
            {privilegeItem.requiredBasicPrivileges && privilegeItem.requiredBasicPrivileges.length > 0
              ? (
                  <p>
                    <RequiredPrivilegeHints
                      requiredBasicPrivileges={privilegeItem.requiredBasicPrivileges || []}
                      parentAdditionalPrivilegeLabels={parentAdditionalPrivilegeLabels}
                      missingPrivilegeHelpText={t('requiredPrivilegeMissing')}
                      missingPrivilegeIconId={Icons.FEEDBACK_ERROR}
                      missingPrivilegeIconModifier="red"
                    />
                  </p>
                )
              : null}
          </div>
        </div>
      </AdditionalPrivilegeContainer>

      {/* the optional subtree, i.e. the list of dependent additional privileges: */}
      {privilegeItem.dependentPrivileges
        ? (
            <div className="privilege-config__additional-privilege-sub-tree">
              <AdditionalPrivilegeTree
                privilegeItems={privilegeItem.dependentPrivileges}
                parentAdditionalPrivilegeLabels={[...parentAdditionalPrivilegeLabels, privilegeItem.label]}
              />
            </div>
          )
        : null}
    </>
  );
}
